import { getVideoFromDB, getImageFromDB } from './indexedDB';

// Helper function to normalize video URLs for cache lookup
function normalizeVideoUrl(url) {
    if (!url) return null;
    
    // Extract the path portion from full URLs
    try {
        // Check if it's a full URL
        if (url.startsWith('http')) {
            const urlObj = new URL(url);
            // Return the pathname (which starts with /)
            return urlObj.pathname;
        }
    } catch (e) {
        console.error('Error parsing URL:', e);
    }
    
    // If it's already a relative path, ensure it starts with /
    if (!url.startsWith('/')) {
        return '/' + url;
    }
    
    // Otherwise return as is
    return url;
}

// Initialize all video elements with offline support on page load
export function initializeOfflineVideos() {
    // Ensure the DOM is fully loaded
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOfflineVideosInternal);
    } else {
        initOfflineVideosInternal();
    }
}

function initOfflineVideosInternal() {
    // Find all video elements on the page
    const videoElements = document.querySelectorAll('video');
    
    if (!videoElements || videoElements.length === 0) {
        return Promise.resolve();
    }
    
    // Set common properties for all videos to improve loading and playback
    Array.from(videoElements).forEach(video => {
        video.preload = 'auto';
        video.muted = true;
        video.setAttribute('playsinline', '');
    });
    
    // Process each video element
    const videoPromises = Array.from(videoElements).map(videoElement => {
        // Get the source URL from either source element or the video src attribute
        let videoPath = null;
        const sourceElement = videoElement.querySelector('source');
        
        if (sourceElement && sourceElement.src) {
            videoPath = sourceElement.src;
        } else if (videoElement.src && videoElement.src !== 'null' && videoElement.src !== 'undefined' && !videoElement.src.includes('blob:')) {
            videoPath = videoElement.src;
        } else if (videoElement.hasAttribute('data-src')) {
            videoPath = videoElement.getAttribute('data-src');
        }
        
        if (!videoPath) {
            console.warn('Video element has no valid source', videoElement);
            return Promise.resolve();
        }
        
        return loadVideoWithOfflineSupport(videoElement, videoPath);
    });
    
    return Promise.all(videoPromises)
        .then(() => {
            return true;
        })
        .catch(error => {
            console.error('Error initializing videos:', error);
            return false;
        });
}

// Helper function to load a video with offline support
function loadVideoWithOfflineSupport(videoElement, videoPath) {
    // Ensure we have a valid path
    if (!videoPath) {
        return Promise.resolve(false);
    }
    
    // Normalize the URL for cache lookup
    const normalizedPath = normalizeVideoUrl(videoPath);
    
    // Track the blob URLs for cleanup
    const blobUrls = [];
    
    // Cleanup function to revoke blob URLs when video element is removed
    const cleanup = () => {
        blobUrls.forEach(url => {
            URL.revokeObjectURL(url);
        });
    };
    
    // Set up observer to clean up blob URLs when video is removed
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (!document.contains(videoElement)) {
                cleanup();
                observer.disconnect();
            }
        });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    // Handle video source
    return getVideoFromDB(normalizedPath)
        .then(videoBlob => {
            if (videoBlob && videoBlob.size > 0) {
                // console.log('Video blob found, rendering from blob:', normalizedPath);
                
                try {
                    // Create object URL from blob
                    const videoBlobUrl = URL.createObjectURL(videoBlob);
                    blobUrls.push(videoBlobUrl);
                    
                    // Set the video source to the blob URL
                    const sourceElement = videoElement.querySelector('source');
                    if (sourceElement) {
                        sourceElement.src = videoBlobUrl;
                    }
                    videoElement.src = videoBlobUrl;
                    
                    // Store the original video path for error handling
                    videoElement.dataset.originalSrc = videoPath;
                    
                    // Error handling - if blob URL fails, fall back to network URL
                    videoElement.addEventListener('error', (e) => {
                        // console.warn('Error with blob URL video, falling back to network URL');
                        
                        // Set the fallback source
                        if (sourceElement) {
                            sourceElement.src = videoPath;
                        }
                        videoElement.src = videoPath;
                        
                        // Force load the new source
                        videoElement.load();
                    });
                    
                    // Ensure metadata is loaded
                    videoElement.addEventListener('loadedmetadata', () => {
                        // console.log('Video metadata loaded successfully');
                    });
                    
                    // Load the new source
                    videoElement.load();
                    
                    return Promise.resolve(true);
                } catch (error) {
                    return setFallbackSource(videoElement, videoPath);
                }
            } else {
                return setFallbackSource(videoElement, videoPath);
            }
        })
        .catch(error => {
            return setFallbackSource(videoElement, videoPath);
        });
}

// Helper to set the fallback source
function setFallbackSource(videoElement, videoPath) {
    try {
        const sourceElement = videoElement.querySelector('source');
        if (sourceElement) {
            sourceElement.src = videoPath;
        } 
        videoElement.src = videoPath;
        
        // Force load the new source
        videoElement.load();
        
        // Register a listener for network errors
        videoElement.addEventListener('error', (e) => {
        }, { once: true });
        
        return Promise.resolve(false);
    } catch (error) {
        return Promise.resolve(false);
    }
}
// Safely attempt to play a video with proper error handling
function attemptToPlayVideo(videoElement) {
    if (!videoElement) {
        return Promise.resolve(false);
    }
    
    // Function to attempt playback
    const playVideo = () => {
        return videoElement.play()
            .then(() => {
                return true;
            })
            .catch(error => {
                return false;
            });
    };
    
    // If the video is already loaded, attempt to play
    if (videoElement.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        return playVideo();
    }
    
    // Otherwise wait for the video to load enough data
    return new Promise((resolve) => {
        const canPlayHandler = () => {
            videoElement.removeEventListener('canplay', canPlayHandler);
            videoElement.removeEventListener('loadeddata', canPlayHandler);
            videoElement.removeEventListener('error', errorHandler);
            
            resolve(playVideo());
        };
        
        const errorHandler = (e) => {
            videoElement.removeEventListener('canplay', canPlayHandler);
            videoElement.removeEventListener('loadeddata', canPlayHandler);
            videoElement.removeEventListener('error', errorHandler);
            
            resolve(false);
        };
        
        videoElement.addEventListener('canplay', canPlayHandler);
        videoElement.addEventListener('loadeddata', canPlayHandler);
        videoElement.addEventListener('error', errorHandler);
    });
}

export function playRandomSequentialVideos() {
    const videoPlayer = document.querySelector('.videoPlayer'); // Select the single video player
    const overlayTexts = document.querySelectorAll('.overlay-text'); // Select all overlay text elements

    function restartCssAnimation(element) {
        element.style.animation = 'none';
        element.offsetHeight;
        element.style.animation = '';
    }

    function setupAndPlayVideo() {
        if (!videoPlayer || overlayTexts.length === 0) {
            console.warn('Video player or overlay texts not found');
            return;
        }

        const playRunId = `${Date.now()}-${Math.random()}`;
        videoPlayer.dataset.playRunId = playRunId;
        videoPlayer.onended = null;

        // Hide all overlay texts initially
        overlayTexts.forEach(text => {
            text.style.display = 'none';
            text.style.animationDelay = '';
        });

        // Randomly select 2 distinct text indexes
        const availableIndexes = Array.from(overlayTexts.keys());
        const firstIndex = Math.floor(Math.random() * availableIndexes.length);
        const firstTextIndex = availableIndexes.splice(firstIndex, 1)[0];
        const secondIndex = Math.floor(Math.random() * availableIndexes.length);
        const secondTextIndex = availableIndexes[secondIndex];

        // Show both texts simultaneously
        const firstText = overlayTexts[firstTextIndex];
        const secondText = overlayTexts[secondTextIndex];
        
        // Set different animation delays for each text
        firstText.style.display = 'block';
        restartCssAnimation(firstText);
        firstText.style.animationDelay = '0s';
        
        secondText.style.display = 'block';
        restartCssAnimation(secondText);
        secondText.style.animationDelay = '5s';

        // Load and play the video
        videoPlayer.load();
        videoPlayer.play().catch(e => console.error("Error attempting to play video: ", e));

        // Reset when video ends
        videoPlayer.onended = () => {
            if (videoPlayer.dataset.playRunId !== playRunId) {
                return;
            }
            overlayTexts.forEach(text => {
                text.style.display = 'none';
            });
        };
    }

    // Call this function each time you want to play the video with new random texts
    setupAndPlayVideo();
}

export function resetVideoPlayer() {
    const videoPlayer = document.querySelector('.videoPlayer');
    const overlayTexts = document.querySelectorAll('.overlay-text');

    overlayTexts.forEach(text => {
        text.style.display = 'none';
        text.style.animationDelay = '';
        text.style.animation = 'none';
        text.offsetHeight;
        text.style.animation = '';
    });

    if (!videoPlayer) {
        return;
    }

    videoPlayer.dataset.playRunId = '';
    videoPlayer.onended = null;

    try {
        videoPlayer.pause();
        videoPlayer.currentTime = 0;
    } catch (e) {
    }
}
